import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CommercialStatusBadge,
  CommercialTypeBadge,
} from '@/modules/commercial/components';
import { listCommercialOperationsForReservation } from '@/modules/commercial/services';
import { buildCommercialOperationNewUrl } from '@/modules/commercial/utils/commercialUrl';
import type { CommercialOperation } from '@/modules/commercial/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type ReservationCommercialPanelProps = {
  reservationId: string;
};

export function ReservationCommercialPanel({
  reservationId,
}: ReservationCommercialPanelProps) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('commercial.read');
  const canWrite = hasPermission('commercial.write');
  const [operations, setOperations] = useState<CommercialOperation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    void listCommercialOperationsForReservation(reservationId)
      .then(setOperations)
      .catch(() => setOperations([]))
      .finally(() => setLoading(false));
  }, [reservationId, canRead]);

  if (!canRead) return null;

  return (
    <div className="inventory-panel">
      <div className="inventory-page-header">
        <h2 className="inventory-panel__subtitle">Operaciones comerciales</h2>
        {canWrite ? (
          <Link
            to={buildCommercialOperationNewUrl({
              reservationId,
              type: 'rental',
            })}
            className="auth-button auth-button--secondary inventory-page-header__btn"
          >
            + Operación
          </Link>
        ) : null}
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {!loading && operations.length === 0 ? (
        <p className="auth-muted">Sin operaciones vinculadas.</p>
      ) : null}
      {!loading && operations.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {operations.map((op) => (
              <tr key={op.id}>
                <td>{op.title}</td>
                <td>
                  <CommercialTypeBadge type={op.type} />
                </td>
                <td>
                  <CommercialStatusBadge status={op.status} />
                </td>
                <td>
                  <Link
                    to={`${modulePaths.commercial}/operaciones/${op.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
